import React, { useLayoutEffect, useRef } from 'react'
import styled from 'styled-components'
import Svalna from '../@types'

const Element = styled.div`
  display: inline;
  /*pointer-events: none;*/
  width: 100%;
`

interface Props extends Svalna.PropWithChildren {
  scroll?: boolean
  behavior?: 'smooth' | 'auto'
  block?: 'center' | 'end' | 'nearest' | 'start'
}

/**
 * check if an element is crollable
 * @param element the element we want to know if it scrollable or not
 * @returns true if the element is scrollable, false otherwise
 */
function isScrollable(element: HTMLElement): boolean {
  const style = window.getComputedStyle(element)
  const overflowY = style.getPropertyValue('overflow-y')
  return (
    element.scrollHeight > element.clientHeight &&
    (overflowY === 'auto' || overflowY === 'scroll')
  )
}

/**
 * Find the first scrolable parent and scrole in it to make the element visible in this parrent.
 * Contrary to scrollIntoView it only scrole the first scrollable parent and not all scrollable the parents.
 * @param element the element that we want to make visible in the parent.
 * @param behavior which type of scroll we want
 * @param block where do we want the element to be aligned vis a vis the parrent
 * @returns void
 */
function scrollToFirstScrollableParent(
  element: HTMLElement,
  behavior: 'smooth' | 'auto',
  block: 'center' | 'end' | 'nearest' | 'start',
): void {
  let scrollableParent: HTMLElement | null = element.parentElement

  while (scrollableParent) {
    if (isScrollable(scrollableParent)) {
      const elementRect = element.getBoundingClientRect()
      const parentRect = scrollableParent.getBoundingClientRect()

      let scrollOffset = 0

      if (block === 'nearest') {
        if (elementRect.top < parentRect.top) {
          scrollOffset = elementRect.top - parentRect.top
        } else if (elementRect.bottom > parentRect.bottom) {
          scrollOffset = elementRect.bottom - parentRect.bottom
        }
      } else if (block === 'start') {
        scrollOffset = elementRect.top - parentRect.top
      } else if (block === 'center') {
        scrollOffset =
          elementRect.top -
          parentRect.top -
          (parentRect.height - elementRect.height) / 2
      } else {
        scrollOffset = elementRect.bottom - parentRect.bottom
      }

      // Scroll the parent
      scrollableParent.scrollBy({
        top: scrollOffset,
        behavior: behavior,
      })
      return // Stop after finding the first scrollable parent
    }
    scrollableParent = scrollableParent.parentElement
  }
  // If no scrollable parent is found, do nothing
}

export function ScrollToElement({
  scroll,
  block = 'nearest',
  behavior = 'smooth',
  children,
}: Props): React.JSX.Element {
  const ref = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    if (ref?.current && scroll) {
      scrollToFirstScrollableParent(ref.current, behavior, block)
    }
  }, [scroll, ref, behavior, block])

  return <Element ref={ref}>{children}</Element>
}
